import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    Users,
    Plus,
    Search,
    Eye,
    Edit,
    Check,
    CheckCircle,
    AlertTriangle,
    Trash2,
    Loader2,
    Building2,
    RotateCw,
    RefreshCw,
    FileText,
    X,
    XCircle,
    Clock,
    Upload,
    History,
    ArrowUpDown,
    ChevronUp,
    ChevronDown,
    Copy,
} from 'lucide-react';
import { apiClient, Driver, CreateDriverRequest, UpdateDriverRequest, Site, IssueSeverity, BreakdownCategoryDefinition, BulkOperationResultsResponse, RecentlyCheckedDriver, ConsentCategorizationPreviewResponse, DbsCheckStatus } from '../services/api';
import DbsStatusBadge from './DBS/DbsStatusBadge';
import AddDriverModal from './Modals/AddDriverModal';
import DriverViewModal from './Modals/DriverViewModal';
import EditDriverModal from './Modals/EditDriverModal';
import BulkCheckResultsModal from './Modals/BulkCheckResultsModal';
import ConsentWarningModal from './Modals/ConsentWarningModal';
import RecentCheckWarningModal from './Modals/RecentCheckWarningModal';
import ConsentCategorizationModal from './Modals/ConsentCategorizationModal';
import SelectionToolbar, { QuickSelectGroup, ToolbarMode } from './SelectionToolbar';
import CSVImportModal from './Modals/CSVImportModal';
import DriversHistoryModal from './Modals/DriversHistoryModal';
import MultiSelectFilter, { MultiSelectFilterOption } from './MultiSelectFilter';
import { getExclusiveQuickIssueSelection, hasValidConsent } from '../utils/driver-helpers';
import { getComplianceExpiryDisplay, getLastCheckDisplay } from '../utils/date-display';
import { useSort } from '../hooks/useSort';

const getQueryFilterValues = (params: URLSearchParams, key: string): string[] => (
    Array.from(new Set(params.getAll(key).flatMap(value => value.split(',')).map(value => value.trim()).filter(Boolean)))
);

const getInitialStatusFilters = (params: URLSearchParams): string[] => {
    const values = getQueryFilterValues(params, 'status');
    if (values.includes('all')) return [];
    const validValues = values.filter(value => ['active', 'inactive'].includes(value));
    return validValues.length > 0 ? validValues : ['active'];
};

interface DriversProps {
    user: any;
}

interface SortHeaderProps {
    label: string;
    active: boolean;
    direction: 'asc' | 'desc';
    onClick: () => void;
    className?: string;
}

const SortHeader: React.FC<SortHeaderProps> = ({ label, active, direction, onClick, className = '' }) => (
    <th scope="col" className={`px-4 py-3.5 text-left ${className}`}>
        <button
            type="button"
            onClick={onClick}
            className={`group relative inline-flex min-h-8 items-center gap-1.5 whitespace-nowrap text-[11px] font-semibold uppercase tracking-wider transition-colors before:absolute before:-inset-x-2 before:-inset-y-1 focus:outline-none focus:ring-2 focus:ring-[#4a8a1c] ${active ? 'text-gray-900' : 'text-gray-500 hover:text-gray-800'}`}
        >
            {label}
            {active
                ? direction === 'asc'
                    ? <ChevronUp className="h-3.5 w-3.5 text-[#4a8a1c]" />
                    : <ChevronDown className="h-3.5 w-3.5 text-[#4a8a1c]" />
                : <ArrowUpDown className="h-3 w-3 text-gray-300 transition-colors group-hover:text-gray-500" />}
        </button>
    </th>
);

const getDriverInitials = (driver: Driver): string => (
    `${driver.firstName?.charAt(0) || ''}${driver.lastName?.charAt(0) || ''}`.toUpperCase() || '–'
);

const RISK_PILL_STYLES: Record<string, { label: string; className: string }> = {
    high: { label: 'High', className: 'bg-red-500' },
    medium: { label: 'Medium', className: 'bg-amber-400' },
    low: { label: 'Low', className: 'bg-[#69BE28]' },
    unknown: { label: 'Unknown', className: 'bg-gray-300' },
};

const ISSUE_ACTION_STYLES: Record<'critical' | 'warning' | 'info', string> = {
    critical: 'text-red-600 focus:ring-red-600',
    warning: 'text-amber-700 focus:ring-amber-700',
    info: 'text-blue-700 focus:ring-blue-700',
};

interface CopyValueButtonProps {
    label: string;
    copied: boolean;
    onCopy: () => void;
}

const CopyValueButton: React.FC<CopyValueButtonProps> = ({ label, copied, onCopy }) => (
    <button
        type="button"
        onClick={(event) => {
            event.stopPropagation();
            onCopy();
        }}
        className={`relative inline-flex h-6 w-6 flex-none items-center justify-center rounded-md transition-all before:absolute before:-inset-2.5 hover:bg-gray-100 hover:text-gray-700 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-[#4a8a1c] ${copied ? 'text-[#4a8a1c] opacity-100' : 'text-gray-500 opacity-40 sm:opacity-0 sm:group-hover:opacity-50 sm:hover:opacity-100'}`}
        title={`Copy ${label.toLowerCase()}`}
        aria-label={`Copy ${label.toLowerCase()}`}
    >
        {copied
            ? <Check className="h-3.5 w-3.5" aria-hidden="true" />
            : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
    </button>
);

interface ComplianceMetricTileProps {
    id: string;
    label: string;
    count: number;
    isLoading: boolean;
    selected: boolean;
    icon: React.ComponentType<{ className?: string }>;
    iconClassName: string;
    iconBackgroundClassName: string;
    tooltip: React.ReactNode;
    onClick: () => void;
}

const ComplianceMetricTile: React.FC<ComplianceMetricTileProps> = ({
    id,
    label,
    count,
    isLoading,
    selected,
    icon: Icon,
    iconClassName,
    iconBackgroundClassName,
    tooltip,
    onClick,
}) => {
    const tooltipId = `${id}-description`;

    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={selected}
            aria-describedby={tooltipId}
            className={`group relative min-h-[88px] w-full rounded-xl border bg-white px-4 py-3 text-left shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.04)] transition-[border-color,background-color,box-shadow] hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 ${selected ? 'border-gray-900' : 'border-gray-200/80'}`}
        >
            <span className={`absolute inset-x-4 top-0 h-0.5 rounded-b-full bg-gray-900 transition-opacity ${selected ? 'opacity-100' : 'opacity-0'}`} aria-hidden="true" />
            <span className="flex items-start justify-between gap-3">
                <span>
                    {isLoading
                        ? <span className="mb-1 block h-7 w-10 animate-pulse rounded-md bg-gray-200" />
                        : <span className="block text-2xl font-bold leading-none tabular-nums text-gray-900">{count}</span>}
                    <span className="mt-2 block text-xs font-medium text-gray-600">{label}</span>
                </span>
                <span className={`flex h-8 w-8 flex-none items-center justify-center rounded-lg ${iconBackgroundClassName}`}>
                    <Icon className={`h-4 w-4 ${iconClassName}`} />
                </span>
            </span>
            <span
                id={tooltipId}
                role="tooltip"
                className="pointer-events-none invisible absolute bottom-full left-1/2 z-50 mb-2 w-64 -translate-x-1/2 rounded-lg bg-gray-900 p-2.5 text-xs font-normal leading-relaxed text-gray-100 opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100 group-focus-visible:visible group-focus-visible:opacity-100"
            >
                {tooltip}
            </span>
        </button>
    );
};

const Drivers: React.FC<DriversProps> = ({ user }) => {
    const [searchParams] = useSearchParams();

    // State management
    const [drivers, setDrivers] = useState<Driver[]>([]);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const isLoading = isInitialLoading || isRefreshing;
    const [error, setError] = useState<string | null>(null);
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const copyResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [baseTotalDrivers, setBaseTotalDrivers] = useState(0);
    const [categoryCount, setCategoryCount] = useState({
        // New compliance-focused counts (7 stat tiles)
        total: 0,
        clearToDrive: 0,
        notClear: 0,
        expiringLicence: 0,
        expiringCpc: 0,
        expiringTacho: 0,
        expiringConsent: 0,
        highRisk: 0,
        overdueChecks: 0,
        consentIssues: 0,
        // Legacy consent counts
        activeConsent: 0,
        pendingConsent: 0,
        noConsent: 0,
        declinedConsent: 0,
        needsAttention: 0
    });
    // Faceted issue counts for the current non-issue filter scope - from backend
    const [issueCounts, setIssueCounts] = useState<{ code: string; label: string; severity: IssueSeverity; count: number }[]>([]);
    // Breakdown category definitions - from backend (single source of truth for Sites page filter labels)
    const [breakdownCategories, setBreakdownCategories] = useState<BreakdownCategoryDefinition[]>([]);

    // Search and filter state - initialize from URL params
    const [searchTerm, setSearchTerm] = useState('');
    const [riskFilters, setRiskFilters] = useState<string[]>(() => getQueryFilterValues(searchParams, 'risk').filter(value => value !== 'all'));
    const [statusFilters, setStatusFilters] = useState<string[]>(() => getInitialStatusFilters(searchParams));
    const [siteFilters, setSiteFilters] = useState<string[]>(() => getQueryFilterValues(searchParams, 'site').filter(value => value !== 'all'));
    const [dueFilter, setDueFilter] = useState(searchParams.get('filter') === 'due');
    const [specialFilter, setSpecialFilter] = useState<string | null>(searchParams.get('show'));
    const [minDays, setMinDays] = useState<number | null>(searchParams.get('minDays') ? parseInt(searchParams.get('minDays')!) : null);
    const [maxDays, setMaxDays] = useState<number | null>(searchParams.get('maxDays') ? parseInt(searchParams.get('maxDays')!) : null);
    const [issueFilters, setIssueFilters] = useState<string[]>([]);
    const [dbsFilters, setDbsFilters] = useState<string[]>(() =>
        getQueryFilterValues(searchParams, 'dbs').filter(v => v !== 'all')
    );
    const rawTabParam = searchParams.get('tab');
    const initialTabParam = (rawTabParam === 'email' ? 'communications' : rawTabParam) as 'driver' | 'licence' | 'documents' | 'consent' | 'dbs' | 'communications' | null;
    const clearToDriveParam = searchParams.get('clearToDrive');
    const [clearToDriveFilter, setClearToDriveFilter] = useState<boolean | null>(
        clearToDriveParam === 'true' ? true : clearToDriveParam === 'false' ? false : null
    );
    const checkedTodayParam = searchParams.get('checkedToday');
    const [checkedTodayFilter, setCheckedTodayFilter] = useState<boolean>(checkedTodayParam === 'true');

    // Sorting state
    type SortField = 'name' | 'risk' | 'site' | 'lastCheck' | 'cpcExpiry' | 'licenceNumber' | 'consent' | 'dbs';
    const { sortBy, sortOrder, toggleSort } = useSort<SortField>('name');

    // Debounced search - delays fetch until user stops typing for 300ms
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Ref to hold the AbortController for the in-flight driver fetch
    const abortControllerRef = useRef<AbortController | null>(null);
    const driversRequestIdRef = useRef(0);
    const hasLoadedDriversRef = useRef(false);

    // Sites state
    const [sites, setSites] = useState<Site[]>([]);
    const [sitesLoading, setSitesLoading] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);


    // Modal states
    const [showAddDriver, setShowAddDriver] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [selectedDriverForView, setSelectedDriverForView] = useState<Driver | null>(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedDriverForEdit, setSelectedDriverForEdit] = useState<Driver | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);

    // Multi-select state for bulk operations
    const [selectedDriverIds, setSelectedDriverIds] = useState<Set<string>>(new Set());
    const [isSelectAllMode, setIsSelectAllMode] = useState(false);
    const [isLoadingSelection, setIsLoadingSelection] = useState(false);
    const [, setSelectionMode] = useState<'check' | 'consent'>('check');
    const [toolbarResetKey, setToolbarResetKey] = useState(0);

    // Bulk operation state
    const [isStartingBulkCheck, setIsStartingBulkCheck] = useState(false);
    const [bulkCheckResults, setBulkCheckResults] = useState<BulkOperationResultsResponse | null>(null);
    const [showResultsModal, setShowResultsModal] = useState(false);
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Bulk consent send state
    const [isStartingBulkConsent, setIsStartingBulkConsent] = useState(false);
    const consentPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // CSV import and history modal state
    const [showCSVImportModal, setShowCSVImportModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);

    // Deactivate / reactivate confirmation modals
    const [deactivateConfirm, setDeactivateConfirm] = useState<{ id: string; name: string } | null>(null);
    const [reactivateConfirm, setReactivateConfirm] = useState<{ id: string; name: string } | null>(null);
    const [isConfirmingDeactivate, setIsConfirmingDeactivate] = useState(false);
    const [isConfirmingReactivate, setIsConfirmingReactivate] = useState(false);

    // Consent warning modal state
    const [showConsentWarning, setShowConsentWarning] = useState(false);
    const [driversWithoutConsent, setDriversWithoutConsent] = useState<Driver[]>([]);
    const [totalSelectedForCheck, setTotalSelectedForCheck] = useState(0);

    // Recent check warning modal state
    const [showRecentCheckWarning, setShowRecentCheckWarning] = useState(false);
    const [recentlyCheckedDrivers, setRecentlyCheckedDrivers] = useState<RecentlyCheckedDriver[]>([]);
    const [driversNeedingCheck, setDriversNeedingCheck] = useState<RecentlyCheckedDriver[]>([]);

    // Consent categorization modal state
    const [showConsentCategorization, setShowConsentCategorization] = useState(false);
    const [consentCategorization, setConsentCategorization] = useState<ConsentCategorizationPreviewResponse['data'] | null>(null);

    const [viewInitialTab, setViewInitialTab] = useState<'driver' | 'licence' | 'documents' | 'consent' | 'dbs' | 'communications'>(initialTabParam ?? 'driver');

    // === MULTI-SELECT HANDLERS ===

    // Handle individual driver selection
    const handleDriverSelect = (driverId: string) => {
        setIsSelectAllMode(false);
        setSelectedDriverIds(prev => {
            const newSelection = new Set(prev);
            if (newSelection.has(driverId)) {
                newSelection.delete(driverId);
            } else {
                newSelection.add(driverId);
            }
            return newSelection;
        });
    };

    const handleCopyValue = async (value: string, label: string, key: string) => {
        try {
            if (!navigator.clipboard?.writeText) {
                throw new Error('Clipboard API is unavailable');
            }

            await navigator.clipboard.writeText(value);
            setCopiedField(key);
            toast.success(`${label} copied`);

            if (copyResetTimeoutRef.current) clearTimeout(copyResetTimeoutRef.current);
            copyResetTimeoutRef.current = setTimeout(() => setCopiedField(null), 1800);
        } catch {
            toast.error(`Could not copy ${label.toLowerCase()}`);
        }
    };

    useEffect(() => () => {
        if (copyResetTimeoutRef.current) clearTimeout(copyResetTimeoutRef.current);
    }, []);

    // Handle select all on current page
    const handleSelectAllOnPage = () => {
        if (isSelectAllMode || selectedDriverIds.size === drivers.length) {
            // Deselect all
            setSelectedDriverIds(new Set());
            setIsSelectAllMode(false);
        } else {
            // Select all on current page
            const allIds = new Set(drivers.map(d => d.id));
            setSelectedDriverIds(allIds);
            setIsSelectAllMode(false);
        }
    };

    // Clear all selection
    const handleClearSelection = () => {
        setSelectedDriverIds(new Set());
        setIsSelectAllMode(false);
        setSelectionMode('check');
        setToolbarResetKey(k => k + 1);
    };

    const handleModeChange = (mode: ToolbarMode) => {
        setSelectionMode(mode === 'idle' ? 'check' : mode);
    };

    // Quick select options grouped for dropdown
    const quickSelectGroups: QuickSelectGroup[] = [
        {
            label: 'By Risk Level',
            options: [
                { label: 'Select All High Risk', value: 'high_risk', description: 'High risk drivers within current filters' },
                { label: 'Select All Medium Risk', value: 'medium_risk', description: 'Medium risk drivers within current filters' },
                { label: 'Select All Low Risk', value: 'low_risk', description: 'Low risk drivers within current filters' },
            ]
        },
        {
            label: 'By Check Status',
            options: [
                { label: 'Select All Due', value: 'due', description: 'Drivers past their recheck date' },
                { label: 'Select All Never Checked', value: 'never_checked', description: 'Drivers with no DVLA check on record' },
                { label: 'Select All Critical Issues', value: 'critical_issues', description: 'Drivers who cannot legally operate' },
            ]
        },
        {
            label: 'Priority',
            options: [
                { label: 'Select All High Risk + Due', value: 'high_risk_due', description: 'Most urgent - high risk and overdue' },
                { label: 'Select All Medium Risk + Due', value: 'medium_risk_due', description: 'Medium risk and overdue' },
            ]
        },
        {
            label: 'By Consent',
            mode: 'consent',
            options: [
                { label: 'Select All No Consent', value: 'no_consent', description: 'Drivers with no consent on file' },
                { label: 'Select All Expired Consent', value: 'expired_consent', description: 'Drivers with expired consent' },
                { label: 'Select All Expiring Soon', value: 'expiring_consent', description: 'Drivers whose consent expires within 30 days' },
            ]
        },
        {
            label: 'Selection Scope',
            isFooter: true,
            options: [
                { label: 'Select All on Current Page', value: 'current_page', description: `Select all ${drivers.length} drivers on this page` },
                { label: 'Select All', value: 'all_drivers', description: 'Select every driver regardless of filters' },
                { label: 'Select All Filtered', value: 'all_filtered', description: `Select all ${totalCount} drivers matching filters` },
            ]
        }
    ];

    // Handle quick select dropdown options
    const handleQuickSelect = async (option: string) => {
        try {
            setIsLoadingSelection(true);

            // Base filters carried from the current page state
            const baseFilters = {
                search: debouncedSearchTerm || undefined,
                status: statusFilters.length > 0 ? statusFilters : 'all',
                siteId: siteFilters.length > 0 ? siteFilters : undefined,
            };

            let selectedIds: Set<string>;

            switch (option) {
                case 'high_risk': {
                    const { ids } = await apiClient.resolveDriverIds({ ...baseFilters, riskLevel: 'high' });
                    selectedIds = new Set(ids);
                    break;
                }
                case 'medium_risk': {
                    const { ids } = await apiClient.resolveDriverIds({ ...baseFilters, riskLevel: 'medium' });
                    selectedIds = new Set(ids);
                    break;
                }
                case 'low_risk': {
                    const { ids } = await apiClient.resolveDriverIds({ ...baseFilters, riskLevel: 'low' });
                    selectedIds = new Set(ids);
                    break;
                }
                case 'due': {
                    const { ids } = await apiClient.resolveDriverIds({
                        ...baseFilters,
                        riskLevel: riskFilters.length > 0 ? riskFilters : undefined,
                        dueForCheck: true,
                    });
                    selectedIds = new Set(ids);
                    break;
                }
                case 'never_checked': {
                    const { ids } = await apiClient.resolveDriverIds({
                        ...baseFilters,
                        riskLevel: riskFilters.length > 0 ? riskFilters : undefined,
                        issue: 'never_checked',
                    });
                    selectedIds = new Set(ids);
                    break;
                }
                case 'critical_issues': {
                    // clearToDrive=false captures all drivers who cannot legally operate
                    const { ids } = await apiClient.resolveDriverIds({
                        ...baseFilters,
                        riskLevel: riskFilters.length > 0 ? riskFilters : undefined,
                        clearToDrive: false,
                    });
                    selectedIds = new Set(ids);
                    break;
                }
                case 'high_risk_due': {
                    const { ids } = await apiClient.resolveDriverIds({
                        ...baseFilters,
                        riskLevel: 'high',
                        dueForCheck: true,
                    });
                    selectedIds = new Set(ids);
                    break;
                }
                case 'medium_risk_due': {
                    const { ids } = await apiClient.resolveDriverIds({
                        ...baseFilters,
                        riskLevel: 'medium',
                        dueForCheck: true,
                    });
                    selectedIds = new Set(ids);
                    break;
                }
                case 'no_consent': {
                    const { ids } = await apiClient.resolveDriverIds({
                        ...baseFilters,
                        riskLevel: riskFilters.length > 0 ? riskFilters : undefined,
                        consentStatus: 'no_consent',
                    });
                    selectedIds = new Set(ids);
                    break;
                }
                case 'expired_consent': {
                    const { ids } = await apiClient.resolveDriverIds({
                        ...baseFilters,
                        riskLevel: riskFilters.length > 0 ? riskFilters : undefined,
                        consentStatus: 'expired',
                    });
                    selectedIds = new Set(ids);
                    break;
                }
                case 'expiring_consent': {
                    const { ids } = await apiClient.resolveDriverIds({
                        ...baseFilters,
                        riskLevel: riskFilters.length > 0 ? riskFilters : undefined,
                        consentStatus: 'expiring_soon',
                    });
                    selectedIds = new Set(ids);
                    break;
                }
                case 'current_page': {
                    selectedIds = new Set(drivers.map(d => d.id));
                    break;
                }
                case 'all_drivers': {
                    const { ids } = await apiClient.resolveDriverIds({});
                    selectedIds = new Set(ids);
                    setIsSelectAllMode(true);
                    break;
                }
                case 'all_filtered': {
                    const { ids } = await apiClient.resolveDriverIds({
                        ...baseFilters,
                        riskLevel: riskFilters.length > 0 ? riskFilters : undefined,
                        issue: specialFilter
                            ? Array.from(new Set([specialFilter, ...issueFilters]))
                            : issueFilters.length > 0 ? issueFilters : undefined,
                        dueForCheck: dueFilter || undefined,
                        clearToDrive: clearToDriveFilter ?? undefined,
                        checkedToday: checkedTodayFilter || undefined,
                        minDays: minDays ?? undefined,
                        maxDays: maxDays ?? undefined,
                    });
                    selectedIds = new Set(ids);
                    setIsSelectAllMode(true);
                    break;
                }
                default:
                    selectedIds = new Set();
            }

            setSelectedDriverIds(selectedIds);

            if (option !== 'all_filtered' && option !== 'all_drivers') {
                setIsSelectAllMode(false);
            }


        } catch (err) {
            console.error('Quick select failed:', err);
            toast.error('Failed to select drivers');
        } finally {
            setIsLoadingSelection(false);
        }
    };

    // === BULK CHECK FLOW HANDLERS ===

    // Poll for operation status
    const pollOperationStatus = async (operationId: string) => {
        try {
            const statusResponse = await apiClient.getBulkOperationStatus(operationId);

            if (statusResponse.data.isComplete) {
                // Stop polling
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }

                // Get the results
                const resultsResponse = await apiClient.getBulkOperationResults(operationId);
                setBulkCheckResults(resultsResponse);
                // Show toast with View Results button
                toast.success(
                    (t) => (
                        <div className="flex items-center justify-between">
                            <span className="mr-3">
                                Bulk check completed! {resultsResponse.data.operation.successCount} successful, {resultsResponse.data.operation.failureCount} failed.
                            </span>
                            <button
                                onClick={() => {
                                    setShowResultsModal(true);
                                    toast.dismiss(t.id);
                                }}
                                className="px-3 py-1 text-xs font-medium text-white rounded-md hover:opacity-90"
                                style={{ backgroundColor: '#69BE28' }}
                            >
                                View Results
                            </button>
                        </div>
                    ),
                    { duration: 15000 }
                );

                // Refresh drivers list
                await refreshDrivers();
            }
        } catch (error) {
            console.error('Error polling operation status:', error);
        }
    };

    // Check for recent licence checks before proceeding
    const checkForRecentChecks = async (driverIds: string[]) => {
        try {
            const response = await apiClient.validateRecentChecks({
                driverIds,
                thresholdDays: 7
            });

            setRecentlyCheckedDrivers(response.data.recentlyCheckedDrivers);
            setDriversNeedingCheck(response.data.driversNeedingCheck);
            setShowRecentCheckWarning(true);
        } catch (err: any) {
            toast.error(err.message || 'Failed to validate recent checks');
            setIsStartingBulkCheck(false);
        }
    };

    // Actually execute the bulk check
    const proceedWithBulkCheck = async (driverIds: string[]) => {
        try {
            const response = await apiClient.startBulkLicenceCheck({ driverIds });
            const operationId = response.data.operationId;

            toast.success(`Bulk licence check started! ${response.data.totalDrivers} drivers in queue.`, {
                duration: 5000
            });

            // Start polling
            await pollOperationStatus(operationId);
            pollingIntervalRef.current = setInterval(() => {
                pollOperationStatus(operationId);
            }, 5000);

            // Clear selection
            handleClearSelection();

        } catch (err: any) {
            toast.error(err.message || 'Failed to start bulk licence check');
        } finally {
            setIsStartingBulkCheck(false);
        }
    };

    // Handle proceed from recent check warning
    const handleProceedWithSelectedDrivers = async (driverIds: string[]) => {
        setShowRecentCheckWarning(false);
        await proceedWithBulkCheck(driverIds);
    };

    // Handle proceed from consent warning
    const handleProceedFromConsentWarning = async () => {
        setShowConsentWarning(false);

        // Calculate drivers with consent by excluding those without consent
        // We already validated consent when showing the modal, so we just need to exclude the ones without
        const driverIdsWithoutConsent = new Set(driversWithoutConsent.map(d => d.id));
        const driverIdsWithConsent = Array.from(selectedDriverIds).filter(id =>
            !driverIdsWithoutConsent.has(id)
        );

        if (driverIdsWithConsent.length === 0) {
            toast.error('No drivers with valid consent to check');
            setIsStartingBulkCheck(false);
            return;
        }

        await checkForRecentChecks(driverIdsWithConsent);
    };

    // Start bulk check (validate consent first)
    const handleStartBulkCheck = async () => {
        if (selectedDriverIds.size === 0) return;

        setIsStartingBulkCheck(true);

        try {
            // For selected drivers on current page, check consent directly
            const selectedDriversOnPage = drivers.filter(d => selectedDriverIds.has(d.id));

            // If we have all selected drivers on current page, check consent directly
            if (selectedDriversOnPage.length === selectedDriverIds.size) {
                const withoutConsent = selectedDriversOnPage.filter(d => !hasValidConsent(d));

                if (withoutConsent.length > 0) {
                    setDriversWithoutConsent(withoutConsent);
                    setTotalSelectedForCheck(selectedDriversOnPage.length);
                    setIsStartingBulkCheck(false);
                    setShowConsentWarning(true);
                    return;
                }

                // All have consent, check for recent checks
                await checkForRecentChecks(selectedDriversOnPage.map(d => d.id));
            } else {
                // Need to fetch all selected drivers to check consent
                const response = await apiClient.getDrivers({
                    page: 1,
                    pageSize: selectedDriverIds.size,
                    status: 'active',
                });

                const selectedDrivers = response.data.filter(d => selectedDriverIds.has(d.id));
                const withoutConsent = selectedDrivers.filter(d => !hasValidConsent(d));

                if (withoutConsent.length > 0) {
                    setDriversWithoutConsent(withoutConsent);
                    setTotalSelectedForCheck(selectedDrivers.length);
                    setIsStartingBulkCheck(false);
                    setShowConsentWarning(true);
                    return;
                }

                // All have consent, check for recent checks
                await checkForRecentChecks(selectedDrivers.map(d => d.id));
            }
        } catch (err: any) {
            toast.error(err.message || 'Failed to start bulk check');
            setIsStartingBulkCheck(false);
        }
    };

    // === BULK CONSENT SEND HANDLERS ===

    const pollConsentOperationStatus = async (operationId: string) => {
        try {
            const statusResponse = await apiClient.getBulkOperationStatus(operationId);
            if (statusResponse.data.isComplete) {
                if (consentPollingIntervalRef.current) {
                    clearInterval(consentPollingIntervalRef.current);
                    consentPollingIntervalRef.current = null;
                }
                toast.success(
                    `Consent forms sent! ${statusResponse.data.successCount} successful, ${statusResponse.data.failureCount} failed.`,
                    { duration: 8000 }
                );
                setIsStartingBulkConsent(false);
            }
        } catch (error) {
            console.error('Error polling consent operation status:', error);
        }
    };

    const handleStartBulkConsent = async () => {
        if (selectedDriverIds.size === 0) return;
        setIsStartingBulkConsent(true);
        try {
            const driverIds = Array.from(selectedDriverIds);
            const response = await apiClient.previewConsentCategorization({
                driverIds
            });

            setConsentCategorization(response.data);
            setShowConsentCategorization(true);
        } catch (err: any) {
            toast.error(err.message || 'Failed to preview consent categorization');
        } finally {
            setIsStartingBulkConsent(false);
        }
    };

    const handleConfirmBulkConsent = async () => {
        setIsStartingBulkConsent(true);
        try {
            const driverIds = Array.from(selectedDriverIds);
            const response = await apiClient.startBulkConsentSend({
                driverIds,
                includePending: true,  // Include drivers with pending consent (send reminders)
                includeValid: false,   // Don't send to drivers with valid consent
            });
            const operationId = response.data.operationId;

            const skipped = response.data.skippedCount || 0;
            const total = response.data.totalDrivers || 0;
            toast.success(`Sending consent forms to ${total} driver${total !== 1 ? 's' : ''}${skipped > 0 ? ` (${skipped} skipped)` : ''}.`, { duration: 6000 });

            // Start polling
            await pollConsentOperationStatus(operationId);
            consentPollingIntervalRef.current = setInterval(() => {
                pollConsentOperationStatus(operationId);
            }, 5000);

            handleClearSelection();
            setShowConsentCategorization(false);
            setConsentCategorization(null);
        } catch (err: any) {
            toast.error(err.message || 'Failed to send consent forms');
        } finally {
            setIsStartingBulkConsent(false);
        }
    };

    // Handle view driver details (modal)
    const handleViewDriver = (driver: Driver, tab: 'driver' | 'licence' | 'consent' | 'dbs' | 'communications' = 'driver') => {
        setSelectedDriverForView(driver);
        setViewInitialTab(tab);
        setShowViewModal(true);
    };

    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setSelectedDriverForView(null);
    };

    const handleEditDriver = (driver: Driver) => {
        setSelectedDriverForEdit(driver);
        setIsEditModalOpen(true);
    };

    const handleUpdateDriver = async (driverId: string, driverData: UpdateDriverRequest) => {
        try {
            setIsUpdating(true);
            await apiClient.updateDriver(driverId, driverData);
            setIsEditModalOpen(false);
            setSelectedDriverForEdit(null);
            await refreshDrivers();
        } catch (err: any) {
            throw err;
        } finally {
            setIsUpdating(false);
        }
    };


    // Load sites on component mount
    useEffect(() => {
        loadSites();
    }, []);

    // Cleanup polling interval on unmount
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, []);

    // Clear selection when filters change
    useEffect(() => {
        setSelectedDriverIds(new Set());
        setIsSelectAllMode(false);
        setToolbarResetKey(k => k + 1);
    }, [debouncedSearchTerm, riskFilters, statusFilters, siteFilters, issueFilters, dueFilter, specialFilter, clearToDriveFilter, checkedTodayFilter, dbsFilters, sortBy, sortOrder]);

    // Handle opening driver modal from URL parameter (e.g., from notifications)
    useEffect(() => {
        const driverId = searchParams.get('id');
        if (!driverId) return;

        // First, try to find driver in current list
        const driverInList = drivers.find(d => d.id === driverId);

        if (driverInList) {
            handleViewDriver(driverInList);
            // Remove the id parameter from URL immediately
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('id');
            window.history.replaceState({}, '', `${window.location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`);
        } else if (!isLoading && drivers.length > 0) {
            // Driver not in current page - fetch all drivers and try again
            const fetchSpecificDriver = async () => {
                try {
                    // Fetch drivers with no filters to find the specific driver
                    const response = await apiClient.getDrivers({
                        search: '',
                        riskLevel: 'all',
                        status: 'active',
                        page: 1,
                        pageSize: 1000, // Large page size to find the driver
                    });

                    const driver = response.data.find(d => d.id === driverId);
                    if (driver) {
                        handleViewDriver(driver);
                    } else {
                        toast.error('Driver not found');
                    }

                    // Remove the id parameter from URL
                    const newSearchParams = new URLSearchParams(searchParams);
                    newSearchParams.delete('id');
                    window.history.replaceState({}, '', `${window.location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`);
                } catch (err) {
                    console.error('Error fetching driver:', err);
                    toast.error('Failed to load driver details');
                    // Still remove the parameter even if there's an error
                    const newSearchParams = new URLSearchParams(searchParams);
                    newSearchParams.delete('id');
                    window.history.replaceState({}, '', `${window.location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`);
                }
            };

            fetchSpecificDriver();
        }
    }, [searchParams, drivers, isLoading]);


    // Sync filter state from URL params when navigating to page
    useEffect(() => {
        const riskParams = getQueryFilterValues(searchParams, 'risk')
            .filter(value => ['low', 'medium', 'high'].includes(value));
        setRiskFilters(riskParams);

        // Handle ?filter=due parameter
        const filterParam = searchParams.get('filter');
        setDueFilter(filterParam === 'due');

        // Handle ?site= parameter
        setSiteFilters(getQueryFilterValues(searchParams, 'site').filter(value => value !== 'all'));

        setStatusFilters(getInitialStatusFilters(searchParams));

        setIssueFilters(getQueryFilterValues(searchParams, 'issue').filter(value => value !== 'all'));

        // Handle ?show= parameter for special filters
        const showParam = searchParams.get('show');
        setSpecialFilter(showParam);

        // Handle ?minDays= and ?maxDays= parameters for expiry date filtering
        const minDaysParam = searchParams.get('minDays');
        const maxDaysParam = searchParams.get('maxDays');
        setMinDays(minDaysParam ? parseInt(minDaysParam) : null);
        setMaxDays(maxDaysParam ? parseInt(maxDaysParam) : null);

        // Handle ?clearToDrive= parameter
        const ctdParam = searchParams.get('clearToDrive');
        setClearToDriveFilter(ctdParam === 'true' ? true : ctdParam === 'false' ? false : null);

        // Handle ?checkedToday= parameter
        const checkedParam = searchParams.get('checkedToday');
        setCheckedTodayFilter(checkedParam === 'true');
    }, [searchParams]);

    // Load drivers on component mount and when filters change.
    // Cancels the previous in-flight request before firing a new one.
    useEffect(() => {
        void refreshDrivers();
        return () => { abortControllerRef.current?.abort(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchTerm, riskFilters, statusFilters, siteFilters, currentPage, dueFilter, specialFilter, minDays, maxDays, issueFilters, clearToDriveFilter, checkedTodayFilter, dbsFilters, sortBy, sortOrder]);

    const loadSites = async () => {
        try {
            setSitesLoading(true);
            const response = await apiClient.getSites({ active: true, pageSize: 1000 });
            setSites(response.data);
        } catch (err) {
            console.error('Error loading sites:', err);
        } finally {
            setSitesLoading(false);
        }
    };

    const loadDrivers = async (signal?: AbortSignal) => {
        const requestId = ++driversRequestIdRef.current;
        const isInitialRequest = !hasLoadedDriversRef.current;
        const isCurrentRequest = () => requestId === driversRequestIdRef.current && !signal?.aborted;

        try {
            if (isInitialRequest) {
                setIsInitialLoading(true);
            } else {
                setIsRefreshing(true);
            }
            setError(null);

            const effectiveIssueFilters = specialFilter
                ? Array.from(new Set([specialFilter, ...issueFilters]))
                : issueFilters;
            const response = await apiClient.getDrivers({
                search: searchTerm,
                riskLevel: riskFilters.length > 0 ? riskFilters : undefined,
                status: statusFilters.length > 0 ? statusFilters : 'all',
                siteId: siteFilters.length > 0 ? siteFilters : undefined,
                issue: effectiveIssueFilters.length > 0 ? effectiveIssueFilters : undefined,
                clearToDrive: clearToDriveFilter ?? undefined,
                checkedToday: checkedTodayFilter || undefined,
                dueForCheck: dueFilter || undefined,
                dbsStatus: dbsFilters.length > 0 ? dbsFilters : undefined,
                minDays: minDays ?? undefined,
                maxDays: maxDays ?? undefined,
                sortBy,
                sortOrder,
                page: currentPage,
                pageSize,
                signal,
            });
            if (!isCurrentRequest()) return;

            setDrivers(response.data);
            setTotalCount(response.totalCount);
            setCategoryCount(response.counts);
            setIssueCounts(response.issueCounts || []);
            setBreakdownCategories(response.breakdownCategories || []);
            setBaseTotalDrivers(previous => previous === 0 && response.counts.total > 0
                ? response.counts.total
                : previous);
        } catch (err: any) {
            // Ignore aborted requests - not a real error
            if (err.name === 'AbortError' || !isCurrentRequest()) return;
            const message = err.message || 'Failed to load drivers';
            if (isInitialRequest) {
                setError(message);
            } else {
                toast.error('Could not update the driver register. Showing the previous results.');
            }
            console.error('Error loading drivers:', err);
        } finally {
            // A stale request must never clear the active request's progress state.
            if (isCurrentRequest()) {
                hasLoadedDriversRef.current = true;
                setIsInitialLoading(false);
                setIsRefreshing(false);
            }
        }
    };

    const refreshDrivers = () => {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;
        return loadDrivers(controller.signal);
    };

    const handleCreateDriver = async (driverData: CreateDriverRequest) => {
        try {
            setIsCreating(true);
            setError(null);

            await apiClient.createDriver(driverData);

            setShowAddDriver(false);
            await refreshDrivers();

        } catch (err: any) {
            setError(err.message || 'Failed to create driver');
            throw err;
        } finally {
            setIsCreating(false);
        }
    };

    const handleDeactivateDriver = (driverId: string, driverName: string) => {
        setDeactivateConfirm({ id: driverId, name: driverName });
    };

    const confirmDeactivateDriver = async () => {
        if (!deactivateConfirm) return;
        try {
            setIsConfirmingDeactivate(true);
            setError(null);
            await apiClient.deactivateDriver(deactivateConfirm.id);
            toast.success(`${deactivateConfirm.name} has been deactivated`);
            setDeactivateConfirm(null);
            await refreshDrivers();
        } catch (err: any) {
            setError(err.message || 'Failed to deactivate driver');
        } finally {
            setIsConfirmingDeactivate(false);
        }
    };

    const handleReactivateDriver = (driverId: string, driverName: string) => {
        setReactivateConfirm({ id: driverId, name: driverName });
    };

    const confirmReactivateDriver = async () => {
        if (!reactivateConfirm) return;
        try {
            setIsConfirmingReactivate(true);
            setError(null);
            const response = await apiClient.reactivateDriver(reactivateConfirm.id);
            toast.success(response.message || 'Driver reactivated successfully');
            setReactivateConfirm(null);
            await refreshDrivers();
        } catch (err: any) {
            const errorMessage = err.message || 'Failed to reactivate driver';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setIsConfirmingReactivate(false);
        }
    };

    const handleStatusFilter = (values: string[]) => {
        setStatusFilters(values);
        setCurrentPage(1);
    };

    const handleSearch = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handleRiskFilter = (values: string[]) => {
        setRiskFilters(values);
        setCurrentPage(1);
    };

    const handleSiteFilter = (values: string[]) => {
        setSiteFilters(values);
        setCurrentPage(1);
    };

    const ISSUE_FILTER_LABELS: Record<string, string> = {
        licence_invalid: 'Driving licence invalid',
        licence_expired: 'Licence expired',
        licence_expiring: 'Licence expiring soon',
        disqualified: 'Disqualified',
        medium_points: '7-8 penalty points',
        high_points: '9+ penalty points',
        never_checked: 'Never checked',
        check_failed: 'Check failed',
        overdue: 'Overdue for recheck',
        serious_offences: 'Serious offences',
        repeat_offence_pattern: 'Repeat offence pattern',
        cpc_expired: 'CPC expired',
        cpc_expiring: 'CPC expiring soon',
        cpc_missing: 'No CPC on record',
        tacho_expired: 'Tachograph card expired',
        tacho_expiring: 'Tachograph card expiring soon',
        tacho_missing: 'No tachograph card on record',
        missing_bus_licence: 'Missing bus licence (D/D1)',
        minibus_only: 'Minibus only (no full D)',
        restricted_hire_reward: 'Not for hire or reward (101)',
        pcv_category_expired: 'PCV category expired',
        pcv_category_expiring: 'PCV category expiring soon',
        pcv_category_provisional: 'PCV category provisional',
        missing_hgv_licence: 'Missing HGV licence (C/CE)',
        hgv_category_expired: 'HGV category expired',
        hgv_category_expiring: 'HGV category expiring soon',
        hgv_category_provisional: 'HGV category provisional',
        missing_car_licence: 'Missing car licence (B)',
        car_category_expired: 'Car licence expired',
        car_category_expiring: 'Car licence expiring soon',
        car_category_provisional: 'Car licence provisional',
        no_consent: 'Consent Issues',
        consent_expiring: 'Consent expiring soon',
        all_expired: 'Any expired document',
        all_expiring: 'Any expiring soon',
    };

    const getIssueFilterLabel = (filter: string): string => {
        return ISSUE_FILTER_LABELS[filter] ?? filter;
    };

    const getConsentStatusDisplay = (driver: Driver) => {
        // Check for withdrawn consent first
        if (driver.consentStatus?.tokens && driver.consentStatus.tokens.length > 0) {
            const latestToken = driver.consentStatus.tokens[0];
            if (latestToken.status === 'withdrawn') {
                return {
                    status: 'Declined',
                    color: 'text-orange-800',
                    bgColor: 'bg-orange-100',
                    icon: XCircle,
                    canSendRequest: true
                };
            }
        }

        // Check for active consent
        if (driver.consentProvided && driver.consentExpiry) {
            const expiryDate = new Date(driver.consentExpiry);
            const now = new Date();
            const thirtyDaysFromNow = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000));

            if (expiryDate < now) {
                return {
                    status: 'Expired',
                    color: 'text-red-800',
                    bgColor: 'bg-red-100',
                    icon: XCircle,
                    canSendRequest: true
                };
            } else if (expiryDate < thirtyDaysFromNow) {
                return {
                    status: 'Expires Soon',
                    color: 'text-yellow-800',
                    bgColor: 'bg-yellow-100',
                    icon: AlertTriangle,
                    canSendRequest: false
                };
            } else {
                return {
                    status: 'Active',
                    color: 'text-green-800',
                    bgColor: 'bg-green-100',
                    icon: CheckCircle,
                    canSendRequest: false
                };
            }
        }

        // Check if we have token status information (pending consent request)
        if (!driver.consentProvided && driver.consentStatus?.tokens && driver.consentStatus.tokens.length > 0) {
            const latestToken = driver.consentStatus.tokens[0];

            // Check token status
            switch (latestToken.status) {
                case 'sent':
                    return {
                        status: 'Sent',
                        color: 'text-blue-800',
                        bgColor: 'bg-blue-100',
                        icon: Clock,
                        canSendRequest: false
                    };
                case 'opened':
                    return {
                        status: 'Opened',
                        color: 'text-purple-800',
                        bgColor: 'bg-purple-100',
                        icon: Clock,
                        canSendRequest: false
                    };
                case 'expired':
                    return {
                        status: 'Link Expired',
                        color: 'text-orange-800',
                        bgColor: 'bg-orange-100',
                        icon: XCircle,
                        canSendRequest: true
                    };
                default:
                    // For other statuses, check if token is still valid
                    if (latestToken.expiresAt && new Date(latestToken.expiresAt) > new Date() &&
                        latestToken.status !== 'withdrawn' && latestToken.status !== 'expired') {
                        return {
                            status: 'Pending',
                            color: 'text-blue-800',
                            bgColor: 'bg-blue-100',
                            icon: Clock,
                            canSendRequest: false
                        };
                    }
            }
        }

        // No consent and no pending request
        return {
            status: 'No Consent',
            color: 'text-gray-800',
            bgColor: 'bg-gray-100',
            icon: Clock,
            canSendRequest: true
        };
    };

    const resetMetricFilters = () => {
        setClearToDriveFilter(null);
        setIssueFilters([]);
        setSpecialFilter(null);
        setMinDays(null);
        setMaxDays(null);
        setRiskFilters([]);
        setDueFilter(false);
        setCheckedTodayFilter(false);
    };

    const handleClearAllFilters = () => {
        handleSearch('');
        setSiteFilters([]);
        setStatusFilters(['active']);
        setDbsFilters([]);
        resetMetricFilters();
        setCurrentPage(1);

        const params = new URLSearchParams(searchParams);
        ['filter', 'show', 'minDays', 'maxDays', 'site', 'risk', 'status', 'issue', 'clearToDrive', 'checkedToday'].forEach(key => params.delete(key));
        window.history.replaceState({}, '', `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`);
    };

    const isDefaultDriverView = !searchTerm
        && siteFilters.length === 0
        && statusFilters.length === 1
        && statusFilters[0] === 'active'
        && clearToDriveFilter === null
        && issueFilters.length === 0
        && specialFilter === null
        && minDays === null
        && maxDays === null
        && riskFilters.length === 0
        && !dueFilter
        && !checkedTodayFilter;

    const metricTiles: ComplianceMetricTileProps[] = [
        {
            id: 'total-drivers',
            label: 'Total',
            count: categoryCount.total,
            isLoading: isInitialLoading,
            selected: isDefaultDriverView,
            icon: Users,
            iconClassName: 'text-[#4a8a1c]',
            iconBackgroundClassName: 'bg-[#69BE28]/10',
            tooltip: 'Total active drivers. Select to clear every driver filter.',
            onClick: handleClearAllFilters,
        },
        {
            id: 'clear-to-drive',
            label: 'Clear to Drive',
            count: categoryCount.clearToDrive,
            isLoading: isInitialLoading,
            selected: clearToDriveFilter === true,
            icon: CheckCircle,
            iconClassName: 'text-green-600',
            iconBackgroundClassName: 'bg-green-50',
            tooltip: 'Drivers with a valid check and no critical compliance issues.',
            onClick: () => {
                const wasSelected = clearToDriveFilter === true;
                resetMetricFilters();
                if (!wasSelected) setClearToDriveFilter(true);
                setCurrentPage(1);
            },
        },
        {
            id: 'not-clear',
            label: 'Not Clear',
            count: categoryCount.notClear,
            isLoading: isInitialLoading,
            selected: clearToDriveFilter === false,
            icon: XCircle,
            iconClassName: 'text-red-600',
            iconBackgroundClassName: 'bg-red-50',
            tooltip: 'Drivers who must not operate until their critical issues are resolved.',
            onClick: () => {
                const wasSelected = clearToDriveFilter === false;
                resetMetricFilters();
                if (!wasSelected) setClearToDriveFilter(false);
                setCurrentPage(1);
            },
        },
        {
            id: 'high-risk',
            label: 'High Risk',
            count: categoryCount.highRisk,
            isLoading: isInitialLoading,
            selected: riskFilters.length === 1 && riskFilters[0] === 'high',
            icon: AlertTriangle,
            iconClassName: 'text-orange-600',
            iconBackgroundClassName: 'bg-orange-50',
            tooltip: 'Drivers who need prioritised review or more frequent checks.',
            onClick: () => {
                const wasSelected = riskFilters.length === 1 && riskFilters[0] === 'high';
                resetMetricFilters();
                if (!wasSelected) setRiskFilters(['high']);
                setCurrentPage(1);
            },
        },
        {
            id: 'overdue-checks',
            label: 'Overdue',
            count: categoryCount.overdueChecks,
            isLoading: isInitialLoading,
            selected: dueFilter,
            icon: RotateCw,
            iconClassName: 'text-blue-600',
            iconBackgroundClassName: 'bg-blue-50',
            tooltip: 'Drivers who are past their scheduled licence recheck date.',
            onClick: () => {
                const wasSelected = dueFilter;
                resetMetricFilters();
                if (!wasSelected) setDueFilter(true);
                setCurrentPage(1);
            },
        },
        {
            id: 'consent-issues',
            label: 'Consent Issues',
            count: categoryCount.consentIssues,
            isLoading: isInitialLoading,
            selected: issueFilters.includes('no_consent') || specialFilter === 'no_consent',
            icon: FileText,
            iconClassName: 'text-gray-600',
            iconBackgroundClassName: 'bg-gray-100',
            tooltip: 'Drivers who need valid consent before automated DVLA checks can run.',
            onClick: () => {
                const wasSelected = issueFilters.includes('no_consent') || specialFilter === 'no_consent';
                resetMetricFilters();
                if (!wasSelected) setIssueFilters(['no_consent']);
                setCurrentPage(1);
            },
        },
    ];

    const siteFilterOptions: MultiSelectFilterOption[] = [
        { value: 'unassigned', label: 'Unassigned' },
        ...sites.map(site => ({
            value: String(site.id),
            label: `${site.name} (${site.code})`,
        })),
    ];
    const riskFilterOptions: MultiSelectFilterOption[] = [
        { value: 'low', label: 'Low Risk' },
        { value: 'medium', label: 'Medium Risk' },
        { value: 'high', label: 'High Risk' },
    ];
    const statusFilterOptions: MultiSelectFilterOption[] = [
        { value: 'active', label: 'Active Drivers' },
        { value: 'inactive', label: 'Inactive Drivers' },
    ];
    const dbsFilterOptions: MultiSelectFilterOption[] = [
        { value: 'none', label: 'No Check Requested' },
        { value: 'action_required', label: 'Action Required' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'clear', label: 'Clear' },
        { value: 'not_clear', label: 'Not Clear' },
    ];
    const issueFilterOptions: MultiSelectFilterOption[] = [...issueCounts]
        .sort((left, right) => {
            const severityOrder: Record<string, number> = { critical: 0, warning: 1, info: 2 };
            const severityDifference = (severityOrder[left.severity] ?? 2) - (severityOrder[right.severity] ?? 2);
            return severityDifference !== 0 ? severityDifference : right.count - left.count;
        })
        .map(issue => ({
            value: issue.code,
            label: issue.label,
            count: issue.count,
        }));
    const issueCountByCode = useMemo(
        () => new Map(issueCounts.map(issue => [issue.code, issue.count])),
        [issueCounts],
    );

    const totalPages = Math.ceil(totalCount / pageSize);

    return (
        <div className="min-h-full" style={{ backgroundColor: '#f3f5f1' }}>
        <div className="mx-auto w-full max-w-[1600px] px-4 py-4 sm:px-6 lg:px-8">

            {/* Print Header */}
            <div className="hidden print:block border-b-2 pb-4 mb-8" style={{ borderColor: '#231f20' }}>
                <div className="flex items-center justify-between">
                    <img
                        src="/logo-horizontal-black.png"
                        alt="IntelliDrive"
                        className="h-20 w-auto"
                    />
                    <div className="text-right">
                        <h1 className="text-lg font-bold text-gray-900">
                            IntelliDrive Compliance Dashboard
                        </h1>
                        <p className="text-sm text-gray-600">
                            Generated: {new Date().toLocaleDateString('en-GB')}
                        </p>
                    </div>
                </div>
            </div>

            {/* Action buttons */}
            <div className="mb-3 flex items-center justify-end gap-2">
                <button
                    type="button"
                    onClick={() => void refreshDrivers()}
                    disabled={isLoading}
                    className="inline-flex min-h-11 items-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#4a8a1c] focus:ring-offset-2 disabled:cursor-wait disabled:opacity-50"
                    title="Refresh driver data"
                    aria-label="Refresh driver data"
                    aria-busy={isLoading}
                >
                    <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                    Refresh
                </button>
                <button
                    type="button"
                    onClick={() => setShowHistoryModal(true)}
                    className="inline-flex min-h-11 items-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#4a8a1c] focus:ring-offset-2"
                    title="View History"
                >
                    <History className="w-4 h-4 mr-2" />
                    History
                </button>
                <button
                    type="button"
                    onClick={() => setShowCSVImportModal(true)}
                    disabled={user?.organisation?.demoMode === true}
                    className={`inline-flex min-h-11 items-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#4a8a1c] focus:ring-offset-2 ${
                        user?.organisation?.demoMode === true
                            ? 'text-gray-400 bg-gray-100 cursor-not-allowed opacity-60'
                            : 'text-gray-700 bg-white hover:bg-gray-50'
                    }`}
                    title="Import CSV"
                >
                    <Upload className="w-4 h-4 mr-2" />
                    Import CSV
                </button>
                <button
                    type="button"
                    onClick={() => setShowAddDriver(true)}
                    className="inline-flex min-h-11 items-center rounded-lg px-3 py-2 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#4a8a1c] focus:ring-offset-2"
                    style={{ backgroundColor: '#3d701a' }}
                    title="Add Driver"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Driver
                </button>
            </div>

            {/* Bulk actions toolbar */}
            <SelectionToolbar
                selectedCount={selectedDriverIds.size}
                totalFilteredCount={totalCount}
                isSelectAllMode={isSelectAllMode}
                isLoading={isLoadingSelection}
                isStartingBulkCheck={isStartingBulkCheck}
                isStartingBulkConsent={isStartingBulkConsent}
                onClearSelection={handleClearSelection}
                onQuickSelect={handleQuickSelect}
                onStartBulkCheck={handleStartBulkCheck}
                onStartBulkConsent={handleStartBulkConsent}
                onModeChange={handleModeChange}
                resetKey={toolbarResetKey}
                quickSelectGroups={quickSelectGroups}
            />

            {/* Compliance summary and primary filters */}
            <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                {metricTiles.map(tile => <ComplianceMetricTile key={tile.id} {...tile} />)}
            </div>

            {/* Expiring soon - compact card */}
            <div className="mb-3 flex items-center gap-4 rounded-xl border border-gray-200/80 bg-white px-4 py-2 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.04)]">
                <div className="flex items-center gap-2 shrink-0">
                    <div className="p-1.5 rounded-lg" style={{ backgroundColor: 'rgba(217,119,6,0.1)' }}>
                        <Clock className="w-4 h-4 text-amber-600" />
                    </div>
                    <span className="whitespace-nowrap text-xs font-semibold text-gray-700">Expiring soon</span>
                </div>
                <div className="w-px h-6 bg-gray-200 shrink-0" />
                <div className="flex items-center gap-3 flex-wrap">
                    {[
                        { label: 'Licence', filter: 'licence_expiring' as const, days: 30 },
                        { label: 'CPC', filter: 'cpc_expiring' as const, days: 90 },
                        { label: 'Tacho', filter: 'tacho_expiring' as const, days: 30 },
                        { label: 'Consent', filter: 'consent_expiring' as const, days: 30 },
                    ].map(({ label, filter, days }) => {
                        const count = issueCountByCode.get(filter) ?? 0;
                        const tooltipId = `${filter}-window`;
                        const selected = issueFilters.includes(filter) || specialFilter === filter;
                        const disabled = count === 0 && !selected;
                        return (
                            <button
                                key={filter}
                                type="button"
                                disabled={disabled}
                                onClick={() => {
                                    setIssueFilters(getExclusiveQuickIssueSelection(
                                        issueFilters,
                                        specialFilter,
                                        filter,
                                    ));
                                    setSpecialFilter(null);
                                    setMinDays(null);
                                    setMaxDays(null);
                                    setCurrentPage(1);
                                }}
                                aria-pressed={selected}
                                aria-describedby={tooltipId}
                                className={`group relative flex min-h-9 items-center gap-3 rounded-lg border px-3 py-1.5 text-xs transition-[border-color,background-color,box-shadow,opacity] before:absolute before:-inset-1 focus:outline-none focus:ring-2 focus:ring-amber-700 focus:ring-offset-2 ${
                                    selected
                                        ? 'border-amber-600 bg-amber-50 shadow-[inset_0_-2px_0_rgba(180,83,9,0.7)]'
                                        : disabled
                                            ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-60'
                                            : 'border-gray-200 bg-white hover:bg-amber-50'
                                }`}
                            >
                                <span className="text-gray-600">{label}</span>
                                <span className={`text-sm font-semibold leading-none tabular-nums ${count === 0 ? 'text-gray-400' : 'text-amber-700'}`}>
                                    {count}
                                </span>
                                <span
                                    id={tooltipId}
                                    role="tooltip"
                                    className="pointer-events-none invisible absolute bottom-full left-1/2 z-50 mb-2 w-36 -translate-x-1/2 rounded-lg bg-gray-900 p-2 text-center text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100 group-focus-visible:visible group-focus-visible:opacity-100"
                                >
                                    {disabled ? `No matching drivers within ${days} days` : `Within ${days} days`}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Filters */}
            <div className="mb-4 rounded-xl border border-gray-200/80 bg-white p-3 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.04)]">
                <div className="flex flex-col gap-3">
                    {/* Row 1: Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name, licence number, or email..."
                            aria-label="Search drivers"
                            className="min-h-11 w-full rounded-md border border-gray-300 py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-[#4a8a1c] focus:ring-offset-2"
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                        />
                    </div>

                    {/* Row 2: Dropdowns */}
                    <div className="flex gap-3">
                        <div className="flex-1">
                            <MultiSelectFilter
                                label="Sites"
                                allLabel="All Sites"
                                options={siteFilterOptions}
                                values={siteFilters}
                                onApply={handleSiteFilter}
                                disabled={sitesLoading}
                                searchable
                            />
                        </div>

                        <div className="flex-1">
                            <MultiSelectFilter
                                label="Risk levels"
                                allLabel="All Risk Levels"
                                options={riskFilterOptions}
                                values={riskFilters}
                                onApply={handleRiskFilter}
                            />
                        </div>

                        <div className="flex-1">
                            <MultiSelectFilter
                                label="Driver status"
                                allLabel="All Drivers"
                                options={statusFilterOptions}
                                values={statusFilters}
                                onApply={handleStatusFilter}
                            />
                        </div>

                        <div className="flex-1">
                            <MultiSelectFilter
                                label="Compliance issues"
                                allLabel="All Issues"
                                options={issueFilterOptions}
                                values={issueFilters}
                                onApply={(values) => {
                                    setIssueFilters(values);
                                    setCurrentPage(1);
                                }}
                                searchable
                            />
                        </div>

                        {/* DBS Status Filter - only shown when DBS add-on is enabled */}
                        {user?.organisation?.trustidEnabled && (
                            <div className="flex-1">
                                <MultiSelectFilter
                                    label="DBS status"
                                    allLabel="All DBS Statuses"
                                    options={dbsFilterOptions}
                                    values={dbsFilters}
                                    onApply={(values) => { setDbsFilters(values); setCurrentPage(1); }}
                                    align="right"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Active filter chips */}
            {(() => {
                const allFilterLabels: Record<string, string> = {
                    'all_expired': 'Any Expired Document',
                    'all_expiring': 'Any Expiring Document',
                    'no_consent': 'Consent Issues',
                    'licence_expiring': 'Licence Expiring within 30 Days',
                    'consent_expiring': 'Consent Expiring within 30 Days',
                };

                const chips: { key: string; label: string; onRemove: () => void }[] = [];

                if (debouncedSearchTerm) chips.push({
                    key: 'search',
                    label: `Search: "${debouncedSearchTerm}"`,
                    onRemove: () => handleSearch(''),
                });

                siteFilters.forEach(siteFilter => {
                    const site = sites.find(s => String(s.id) === siteFilter);
                    chips.push({
                        key: `site-${siteFilter}`,
                        label: `Site: ${siteFilter === 'unassigned' ? 'Unassigned' : site?.name ?? siteFilter}`,
                        onRemove: () => {
                            setSiteFilters(current => current.filter(value => value !== siteFilter));
                            setCurrentPage(1);
                        },
                    });
                });

                riskFilters.forEach(riskFilter => chips.push({
                    key: `risk-${riskFilter}`,
                    label: `Risk: ${riskFilter.charAt(0).toUpperCase() + riskFilter.slice(1)}`,
                    onRemove: () => {
                        setRiskFilters(current => current.filter(value => value !== riskFilter));
                        setCurrentPage(1);
                    },
                }));

                const isDefaultStatus = statusFilters.length === 1 && statusFilters[0] === 'active';
                if (!isDefaultStatus && statusFilters.length === 0) chips.push({
                    key: 'status-all',
                    label: 'All Drivers',
                    onRemove: () => { setStatusFilters(['active']); setCurrentPage(1); },
                });
                if (!isDefaultStatus) statusFilters.forEach(statusFilter => chips.push({
                    key: `status-${statusFilter}`,
                    label: statusFilter === 'active' ? 'Active Drivers' : 'Inactive Drivers',
                    onRemove: () => {
                        setStatusFilters(current => current.filter(value => value !== statusFilter));
                        setCurrentPage(1);
                    },
                }));

                if (dueFilter) chips.push({
                    key: 'due',
                    label: 'Overdue Checks',
                    onRemove: () => {
                        setDueFilter(false);
                        setCurrentPage(1);
                        const p = new URLSearchParams(searchParams);
                        p.delete('filter');
                        window.history.replaceState({}, '', `${window.location.pathname}${p.toString() ? '?' + p.toString() : ''}`);
                    },
                });

                if (specialFilter) {
                    const category = breakdownCategories.find(c => c.filter === specialFilter);
                    const inAllFilterLabels = specialFilter in allFilterLabels;
                    const label = allFilterLabels[specialFilter] || category?.label || specialFilter;
                    const daysText = maxDays && !inAllFilterLabels ? ` (${(minDays || 0) + 1}–${maxDays} days)` : '';
                    chips.push({
                        key: 'special',
                        label: label + daysText,
                        onRemove: () => {
                            setSpecialFilter(null);
                            setMinDays(null);
                            setMaxDays(null);
                            setCurrentPage(1);
                            const p = new URLSearchParams(searchParams);
                            ['show', 'minDays', 'maxDays', 'site'].forEach(k => p.delete(k));
                            window.history.replaceState({}, '', `${window.location.pathname}${p.toString() ? '?' + p.toString() : ''}`);
                        },
                    });
                }

                issueFilters.forEach(issueFilter => chips.push({
                    key: `issue-${issueFilter}`,
                    label: getIssueFilterLabel(issueFilter),
                    onRemove: () => {
                        setIssueFilters(current => current.filter(value => value !== issueFilter));
                        setCurrentPage(1);
                    },
                }));

                if (clearToDriveFilter !== null) chips.push({
                    key: 'clearToDrive',
                    label: clearToDriveFilter ? 'Clear to Drive' : 'Not Clear to Drive',
                    onRemove: () => { setClearToDriveFilter(null); setCurrentPage(1); },
                });

                if (checkedTodayFilter) chips.push({
                    key: 'checkedToday',
                    label: 'Checked Today',
                    onRemove: () => { setCheckedTodayFilter(false); setCurrentPage(1); },
                });

                if (user?.organisation?.trustidEnabled && dbsFilters.length > 0) {
                    const dbsLabels: Record<string, string> = {
                        none: 'No Check Requested',
                        action_required: 'Action Required',
                        in_progress: 'In Progress',
                        clear: 'Clear',
                        not_clear: 'Not Clear',
                    };
                    dbsFilters.forEach(status => {
                        chips.push({
                            key: `dbs-${status}`,
                            label: `DBS: ${dbsLabels[status] ?? status}`,
                            onRemove: () => { setDbsFilters(current => current.filter(v => v !== status)); setCurrentPage(1); },
                        });
                    });
                }

                if (chips.length === 0) return null;

                return (
                    <div className="mb-4 flex items-start">
                        <div className="flex flex-wrap items-center gap-2">
                            {chips.length > 0 && (
                                <span className="mr-1 text-xs font-medium uppercase tracking-wider text-gray-500">Filters</span>
                            )}
                            {chips.map(chip => (
                                <span
                                    key={chip.key}
                                    className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white py-1 pl-2.5 pr-1.5 text-sm font-medium text-gray-700 shadow-[0_1px_1px_rgba(15,23,42,0.03)]"
                                >
                                    {chip.label}
                                    <button
                                        type="button"
                                        onClick={chip.onRemove}
                                        className="relative ml-0.5 rounded-full p-1 text-gray-500 transition-colors before:absolute before:-inset-1.5 hover:bg-[#69BE28]/10 hover:text-[#4a8a1c] focus:outline-none focus:ring-2 focus:ring-[#4a8a1c] focus:ring-offset-1"
                                        aria-label={`Remove filter: ${chip.label}`}
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                </span>
                            ))}
                            {chips.length > 1 && (
                                <button
                                    type="button"
                                    onClick={handleClearAllFilters}
                                    className="min-h-8 rounded-md px-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#4a8a1c]"
                                >
                                    Clear all
                                </button>
                            )}
                        </div>
                    </div>
                );
            })()}

            {/* Error Display */}
            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                    <div className="flex items-center">
                        <AlertTriangle className="w-4 h-4 text-red-500 mr-2" />
                        <span className="text-red-800">{error}</span>
                    </div>
                </div>
            )}

            {/* Drivers Table */}
            <div
                className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)]"
                aria-busy={isRefreshing}
            >
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#69BE28]/10">
                            <Users className="h-4 w-4 text-[#4a8a1c]" />
                        </div>
                        <div>
                            <div className="flex items-center gap-1.5">
                                <h3 className="text-sm font-semibold text-gray-900">Driver register</h3>
                                <Loader2
                                    className={`h-3.5 w-3.5 text-[#4a8a1c] transition-opacity motion-reduce:animate-none ${isRefreshing ? 'animate-spin opacity-100' : 'opacity-0'}`}
                                    aria-hidden="true"
                                />
                            </div>
                            <p className="mt-0.5 text-xs text-gray-500">
                                {baseTotalDrivers > totalCount
                                    ? `${totalCount} matching of ${baseTotalDrivers} drivers`
                                    : `${totalCount} ${totalCount === 1 ? 'driver' : 'drivers'}`}
                            </p>
                        </div>
                    </div>
                </div>
                {isInitialLoading ? (
                    <div className="p-10 text-center">
                        <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#69BE28]" />
                        <p className="mt-2 text-sm text-gray-500">Loading drivers…</p>
                    </div>
                ) : drivers.length === 0 ? (
                    <div className="p-10 text-center">
                        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-100">
                            <Users className="h-6 w-6 text-gray-400" />
                        </div>
                        <h3 className="mt-3 text-sm font-semibold text-gray-900">No drivers found</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {searchTerm ? 'Clear or adjust the current filters to see more drivers.' : 'Add your first driver to start managing fleet compliance.'}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[1180px] table-fixed">
                            <caption className="sr-only">Driver compliance register</caption>
                            <thead className="sticky top-0 z-10 border-b border-gray-100 bg-gray-50/95 backdrop-blur-sm">
                                <tr>
                                    <th scope="col" className="w-10 p-0 text-left">
                                        <label className="flex h-11 w-10 cursor-pointer items-center justify-center" title="Select all on this page">
                                            <input
                                                type="checkbox"
                                                ref={(el) => {
                                                    if (el) {
                                                        el.indeterminate = !isSelectAllMode &&
                                                            selectedDriverIds.size > 0 &&
                                                            selectedDriverIds.size < drivers.length;
                                                    }
                                                }}
                                                checked={isSelectAllMode || (selectedDriverIds.size === drivers.length && drivers.length > 0)}
                                                onChange={handleSelectAllOnPage}
                                                className="h-4 w-4 cursor-pointer rounded border-gray-300 focus:ring-2 focus:ring-[#4a8a1c]"
                                                style={{ accentColor: '#69BE28' }}
                                                aria-label="Select all drivers on this page"
                                            />
                                        </label>
                                    </th>
                                    <SortHeader label="Driver" active={sortBy === 'name'} direction={sortOrder} onClick={() => toggleSort('name')} className="w-[225px] !px-3" />
                                    <th scope="col" className="w-[125px] px-4 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                                        Open issues
                                    </th>
                                    <SortHeader label="Risk" active={sortBy === 'risk'} direction={sortOrder} onClick={() => toggleSort('risk')} className="w-[85px]" />
                                    <SortHeader label="Consent" active={sortBy === 'consent'} direction={sortOrder} onClick={() => toggleSort('consent')} className="w-[130px]" />
                                    {user?.organisation?.trustidEnabled && (
                                        <SortHeader label="DBS" active={sortBy === 'dbs'} direction={sortOrder} onClick={() => toggleSort('dbs')} className="w-[140px]" />
                                    )}
                                    <SortHeader label="Site" active={sortBy === 'site'} direction={sortOrder} onClick={() => toggleSort('site')} className="w-[145px]" />
                                    <SortHeader label="Last check" active={sortBy === 'lastCheck'} direction={sortOrder} onClick={() => toggleSort('lastCheck')} className="w-[155px]" />
                                    <SortHeader label="CPC expiry" active={sortBy === 'cpcExpiry'} direction={sortOrder} onClick={() => toggleSort('cpcExpiry')} className="w-[125px]" />
                                    <th scope="col" className="w-[125px] px-4 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                                {drivers.map((driver) => {
                                    const issues = driver.issues || [];
                                    const worstSeverity = issues.reduce<'critical' | 'warning' | 'info'>((worst, issue) => {
                                        if (issue.severity === 'critical') return 'critical';
                                        if (issue.severity === 'warning' && worst !== 'critical') return 'warning';
                                        return worst;
                                    }, 'info');
                                    const risk = driver.riskLevel || 'unknown';
                                    const riskStyle = RISK_PILL_STYLES[risk] || RISK_PILL_STYLES.unknown;
                                    const consent = getConsentStatusDisplay(driver);
                                    const consentBarClass = consent.status === 'Active'
                                        ? 'bg-green-500'
                                        : consent.status === 'Expires Soon'
                                            ? 'bg-amber-400'
                                            : ['Sent', 'Opened', 'Pending'].includes(consent.status)
                                                ? 'bg-blue-500'
                                                : 'bg-red-500';
                                    const lastCheck = getLastCheckDisplay(driver.lastLicenceCheck);
                                    const cpc = getComplianceExpiryDisplay(driver.cpcExpiryDate);
                                    const cpcClass = cpc.status === 'expired'
                                        ? 'text-red-600'
                                        : cpc.status === 'due-soon'
                                            ? 'text-amber-700'
                                            : cpc.status === 'missing'
                                                ? 'text-gray-500'
                                                : 'text-gray-700';
                                    const selected = selectedDriverIds.has(driver.id);

                                    return (
                                    <tr key={driver.id} className={`group transition-colors ${selected ? 'bg-green-50/70' : 'hover:bg-[#f8faf6]'}`}>
                                        <td className="p-0">
                                            <label className="flex h-11 w-10 cursor-pointer items-center justify-center" title={`Select ${driver.firstName} ${driver.lastName}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={selected}
                                                    onChange={() => handleDriverSelect(driver.id)}
                                                    className="h-4 w-4 cursor-pointer rounded border-gray-300 focus:ring-2 focus:ring-[#4a8a1c]"
                                                    style={{ accentColor: '#69BE28' }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    aria-label={`Select ${driver.firstName} ${driver.lastName}`}
                                                />
                                            </label>
                                        </td>
                                        <td className="px-3 py-3.5">
                                            <div className="flex max-w-[220px] items-center gap-3 text-left">
                                                <button
                                                    type="button"
                                                    onClick={() => handleViewDriver(driver)}
                                                    className="relative flex h-9 w-9 flex-none items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600 transition-colors before:absolute before:-inset-1 hover:bg-[#69BE28]/10 hover:text-[#4a8a1c] focus:outline-none focus:ring-2 focus:ring-[#4a8a1c]"
                                                    title={`View ${driver.firstName} ${driver.lastName}`}
                                                    aria-label={`View ${driver.firstName} ${driver.lastName}`}
                                                >
                                                    {getDriverInitials(driver)}
                                                </button>
                                                <span className="min-w-0 flex-1">
                                                    <span className="flex items-center gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleViewDriver(driver)}
                                                            className="min-w-0 truncate rounded-sm text-sm font-semibold text-gray-900 transition-colors hover:text-[#4a8a1c] focus:outline-none focus:ring-2 focus:ring-[#4a8a1c]"
                                                            title={`View ${driver.firstName} ${driver.lastName}`}
                                                        >
                                                            {driver.firstName} {driver.lastName}
                                                        </button>
                                                        <CopyValueButton
                                                            label={`Driver name for ${driver.firstName} ${driver.lastName}`}
                                                            copied={copiedField === `${driver.id}:name`}
                                                            onCopy={() => handleCopyValue(`${driver.firstName} ${driver.lastName}`.trim(), 'Driver name', `${driver.id}:name`)}
                                                        />
                                                        {!driver.active && <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gray-500">Inactive</span>}
                                                    </span>
                                                    <span className="mt-0.5 flex min-w-0 items-center gap-1 text-xs text-gray-500">
                                                        <span className="truncate">{driver.email}</span>
                                                        <CopyValueButton
                                                            label={`Email address for ${driver.firstName} ${driver.lastName}`}
                                                            copied={copiedField === `${driver.id}:email`}
                                                            onCopy={() => handleCopyValue(driver.email, 'Email address', `${driver.id}:email`)}
                                                        />
                                                    </span>
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3.5">
                                            {driver.clearToDrive === null || driver.clearToDrive === undefined ? (
                                                <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-gray-500">
                                                    <Clock className="h-3.5 w-3.5" />Not checked
                                                </span>
                                            ) : driver.clearToDrive === true && issues.length === 0 ? (
                                                <span
                                                    className="inline-flex min-h-7 items-center px-1 text-base leading-none text-gray-300"
                                                    title="No open issues"
                                                    aria-label="No open issues"
                                                >
                                                    -
                                                </span>
                                            ) : issues.length > 0 ? (
                                                <button
                                                    type="button"
                                                    onClick={() => handleViewDriver(driver, 'licence')}
                                                    className={`relative inline-flex min-h-7 items-center gap-1.5 whitespace-nowrap rounded-md px-1 text-xs font-semibold underline-offset-4 transition-colors before:absolute before:-inset-1 hover:underline focus:outline-none focus:ring-2 focus:ring-offset-2 ${ISSUE_ACTION_STYLES[worstSeverity]}`}
                                                    title={`Open ${issues.length} licence issue${issues.length === 1 ? '' : 's'}`}
                                                    aria-label={`Open ${issues.length} licence issue${issues.length === 1 ? '' : 's'} for ${driver.firstName} ${driver.lastName}`}
                                                >
                                                    <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                                                    <span className="tabular-nums">{issues.length > 99 ? '99+' : issues.length} open</span>
                                                </button>
                                            ) : (
                                                <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-red-600">
                                                    <X className="h-3.5 w-3.5" />Not clear
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3.5">
                                            <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700">
                                                <span className={`h-4 w-[3px] rounded-full ${riskStyle.className}`} aria-hidden="true" />
                                                {riskStyle.label}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3.5">
                                            <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700">
                                                <span className={`h-4 w-[3px] rounded-full ${consentBarClass}`} aria-hidden="true" />{consent.status}
                                            </span>
                                        </td>
                                        {user?.organisation?.trustidEnabled && (
                                            <td className="px-4 py-3.5">
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); handleViewDriver(driver, 'dbs'); }}
                                                    className="rounded-sm focus:outline-none focus:ring-2 focus:ring-[#4a8a1c]"
                                                >
                                                    <DbsStatusBadge
                                                        status={driver.dbsStatus as DbsCheckStatus | undefined}
                                                        clear={driver.dbsClear}
                                                        variant="inline"
                                                    />
                                                </button>
                                            </td>
                                        )}
                                        <td className="px-4 py-3.5">
                                            <div className="flex items-center gap-2 text-sm text-gray-700" title={driver.site ? `${driver.site.name} (${driver.site.code})` : undefined}>
                                                {driver.site && <Building2 className="h-3.5 w-3.5 flex-none text-gray-300" />}
                                                <span className={`max-w-[150px] truncate ${driver.site ? '' : 'text-base leading-none text-gray-300'}`}>{driver.site?.name || '-'}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3.5">
                                            {lastCheck.dateTime ? (
                                                <div className="flex items-start gap-1">
                                                    <time dateTime={lastCheck.dateTime} className="block">
                                                        <span className="block whitespace-nowrap text-sm font-medium tabular-nums text-gray-700">{lastCheck.primary}</span>
                                                        <span className="mt-0.5 block whitespace-nowrap text-xs text-gray-500">{lastCheck.secondary}</span>
                                                    </time>
                                                    <CopyValueButton
                                                        label={`Last check for ${driver.firstName} ${driver.lastName}`}
                                                        copied={copiedField === `${driver.id}:last-check`}
                                                        onCopy={() => handleCopyValue(lastCheck.copyValue!, 'Last check', `${driver.id}:last-check`)}
                                                    />
                                                </div>
                                            ) : (
                                                <>
                                                    <p className="whitespace-nowrap text-sm font-medium text-gray-500">{lastCheck.primary}</p>
                                                    <p className="mt-0.5 whitespace-nowrap text-xs text-gray-500">{lastCheck.secondary}</p>
                                                </>
                                            )}
                                        </td>
                                        <td className="px-4 py-3.5">
                                            {cpc.dateTime ? (
                                                <div className="flex items-start gap-1">
                                                    <time dateTime={cpc.dateTime} className="block">
                                                        <span className={`block whitespace-nowrap text-sm font-medium tabular-nums ${cpcClass}`}>{cpc.date}</span>
                                                        {cpc.relative && (
                                                            <span className={`mt-0.5 block whitespace-nowrap text-xs font-medium tabular-nums ${cpcClass}`}>{cpc.relative}</span>
                                                        )}
                                                    </time>
                                                    <CopyValueButton
                                                        label={`CPC expiry for ${driver.firstName} ${driver.lastName}`}
                                                        copied={copiedField === `${driver.id}:cpc-expiry`}
                                                        onCopy={() => handleCopyValue(cpc.date, 'CPC expiry', `${driver.id}:cpc-expiry`)}
                                                    />
                                                </div>
                                            ) : (
                                                <>
                                                    <p className="whitespace-nowrap text-sm font-medium text-gray-500">{cpc.date}</p>
                                                    <p className="mt-0.5 whitespace-nowrap text-xs font-medium text-gray-500">{cpc.relative}</p>
                                                </>
                                            )}
                                        </td>
                                        <td className="px-4 py-3.5">
                                            <div className="flex items-center justify-end gap-1.5">
                                                <button
                                                    type="button"
                                                    onClick={() => handleViewDriver(driver)}
                                                    className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors before:absolute before:-inset-1 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#4a8a1c]"
                                                    title="View driver details"
                                                    aria-label={`View ${driver.firstName} ${driver.lastName}`}
                                                >
                                                    <Eye className="h-4 w-4" aria-hidden="true" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleEditDriver(driver)}
                                                    className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors before:absolute before:-inset-1 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#4a8a1c]"
                                                    title="Edit driver"
                                                    aria-label={`Edit ${driver.firstName} ${driver.lastName}`}
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </button>
                                                {driver.active ? (
                                                    <div className="group/tip relative inline-flex">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDeactivateDriver(driver.id, `${driver.firstName} ${driver.lastName}`)}
                                                            className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors before:absolute before:-inset-1 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-600"
                                                            aria-label={`Deactivate ${driver.firstName} ${driver.lastName}`}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                        <span className="pointer-events-none absolute bottom-full right-0 z-[100] mb-1.5 whitespace-nowrap rounded-lg bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover/tip:opacity-100">Deactivate driver</span>
                                                    </div>
                                                ) : (
                                                    <div className="group/tip relative inline-flex">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleReactivateDriver(driver.id, `${driver.firstName} ${driver.lastName}`)}
                                                            className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors before:absolute before:-inset-1 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-700"
                                                            aria-label={`Reactivate ${driver.firstName} ${driver.lastName}`}
                                                        >
                                                            <RotateCw className="h-4 w-4" />
                                                        </button>
                                                        <span className="pointer-events-none absolute bottom-full right-0 z-[100] mb-1.5 whitespace-nowrap rounded-lg bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover/tip:opacity-100">Reactivate driver</span>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="mt-5 flex justify-between items-center">
                    <div className="text-sm text-gray-500">
                        Page {currentPage} of {totalPages}
                    </div>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="min-h-11 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#4a8a1c] disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            Previous
                        </button>
                        <button
                            type="button"
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="min-h-11 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#4a8a1c] disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

        </div>{/* /wide data-workspace container */}

            {/* Add Driver Modal */}
            <AddDriverModal
                isOpen={showAddDriver}
                onClose={() => setShowAddDriver(false)}
                onSubmit={handleCreateDriver}
                isCreating={isCreating}
            />

            {/* View Driver Modal */}
            <DriverViewModal
                driver={selectedDriverForView}
                isOpen={showViewModal}
                onClose={handleCloseViewModal}
                onDriverUpdated={loadDrivers}
                subtype={user?.organisation?.subtype}
                initialTab={viewInitialTab}
                user={user}
            />

            {/* Edit Driver Modal */}
            <EditDriverModal
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setSelectedDriverForEdit(null);
                }}
                onSubmit={handleUpdateDriver}
                isUpdating={isUpdating}
                driver={selectedDriverForEdit}
            />


            {/* === MULTI-SELECT BULK CHECK COMPONENTS === */}

            {/* Bulk Check Results Modal */}
            <BulkCheckResultsModal
                isOpen={showResultsModal}
                onClose={() => setShowResultsModal(false)}
                results={bulkCheckResults}
                subtype={user?.organisation?.subtype}
            />

            {/* Consent Warning Modal */}
            <ConsentWarningModal
                isOpen={showConsentWarning}
                onClose={() => {
                    setShowConsentWarning(false);
                    setIsStartingBulkCheck(false);
                }}
                onConfirm={handleProceedFromConsentWarning}
                onCancel={() => {
                    setShowConsentWarning(false);
                    setDriversWithoutConsent([]);
                    setTotalSelectedForCheck(0);
                    setIsStartingBulkCheck(false);
                }}
                isConfirming={isStartingBulkCheck}
                driversWithoutConsent={driversWithoutConsent}
                totalSelectedDrivers={totalSelectedForCheck}
            />

            {/* CSV Import Modal */}
            <CSVImportModal
                isOpen={showCSVImportModal}
                onClose={() => setShowCSVImportModal(false)}
                onImportComplete={() => {
                    setShowCSVImportModal(false);
                    void refreshDrivers();
                }}
            />

            {/* Drivers History Modal */}
            <DriversHistoryModal
                isOpen={showHistoryModal}
                onClose={() => setShowHistoryModal(false)}
                subtype={user?.organisation?.subtype}
            />

            {/* Recent Check Warning Modal */}
            <RecentCheckWarningModal
                isOpen={showRecentCheckWarning}
                onClose={() => {
                    setShowRecentCheckWarning(false);
                    setIsStartingBulkCheck(false);
                }}
                recentlyCheckedDrivers={recentlyCheckedDrivers}
                driversNeedingCheck={driversNeedingCheck}
                onProceed={handleProceedWithSelectedDrivers}
                thresholdDays={7}
            />

            {/* Consent Categorization Modal */}
            <ConsentCategorizationModal
                isOpen={showConsentCategorization}
                onClose={() => {
                    setShowConsentCategorization(false);
                    setConsentCategorization(null);
                }}
                categorization={consentCategorization}
                onProceed={handleConfirmBulkConsent}
                isLoading={isStartingBulkConsent}
            />

            {/* Deactivate Driver Confirmation Modal */}
            {deactivateConfirm && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => !isConfirmingDeactivate && setDeactivateConfirm(null)} />
                        <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="p-2 bg-red-100 rounded-lg">
                                    <Trash2 className="w-5 h-5 text-red-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Deactivate Driver</h3>
                            </div>
                            <p className="text-sm text-gray-600 mb-1">
                                Are you sure you want to deactivate <span className="font-medium text-gray-900">{deactivateConfirm.name}</span>?
                            </p>
                            <p className="text-sm text-gray-500 mb-6">
                                They will no longer appear in active driver lists and automated checks will be skipped.
                            </p>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setDeactivateConfirm(null)}
                                    disabled={isConfirmingDeactivate}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDeactivateDriver}
                                    disabled={isConfirmingDeactivate}
                                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
                                >
                                    {isConfirmingDeactivate && <Loader2 className="w-4 h-4 animate-spin" />}
                                    <span>{isConfirmingDeactivate ? 'Deactivating...' : 'Deactivate'}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reactivate Driver Confirmation Modal */}
            {reactivateConfirm && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => !isConfirmingReactivate && setReactivateConfirm(null)} />
                        <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <RotateCw className="w-5 h-5 text-blue-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Reactivate Driver</h3>
                            </div>
                            <p className="text-sm text-gray-600 mb-1">
                                Are you sure you want to reactivate <span className="font-medium text-gray-900">{reactivateConfirm.name}</span>?
                            </p>
                            <p className="text-sm text-gray-500 mb-6">
                                They will be added back to active driver lists and will need to provide consent again before checks can be performed.
                            </p>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setReactivateConfirm(null)}
                                    disabled={isConfirmingReactivate}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmReactivateDriver}
                                    disabled={isConfirmingReactivate}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                                >
                                    {isConfirmingReactivate && <Loader2 className="w-4 h-4 animate-spin" />}
                                    <span>{isConfirmingReactivate ? 'Reactivating...' : 'Reactivate'}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Issues Modal */}
        </div>
    );
};

export default Drivers;
